import { useAuth0 } from '@auth0/auth0-react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import useCompanyId from '@/Hooks/UseCompanyId';
import { EmailTemplate, EmailTemplateType, Notifications } from '@/Models/Configuration';
import http from '@/Utils/HTTP/NewHttpUtils';

export interface AccountSettings {
    rejectEmailTemplate: EmailTemplate;
    inviteEmailTemplate: EmailTemplate;
    notifications: Notifications;
}

const DefaultAccountSettings: AccountSettings = {
    inviteEmailTemplate: {
        title: '',
        thankYouMessage: '',
        bodyHtml: '',
        regards: '',
        templateHtml: '',
    },
    rejectEmailTemplate: {
        title: '',
        thankYouMessage: '',
        bodyHtml: '',
        regards: '',
        templateHtml: '',
    },
    notifications: { sendNotificationsOnAssessmentComplete: true },
};

export const useAccountSettings = () => {
    const companyId = useCompanyId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWR(
        `settings/company/${companyId}/account`,
        async (path) => {
            const result = await http.get<AccountSettings>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return result;
        },
        { fallbackData: DefaultAccountSettings },
    );
};

interface UpdateNotificationSettingsRequest {
    sendNotificationsOnAssessmentComplete: boolean;
}

export const useUpdateNotificationSettings = () => {
    const companyId = useCompanyId();
    const { getAccessTokenSilently } = useAuth0();
    const { mutate } = useAccountSettings();

    return useSWRMutation(
        `settings/company/${companyId}/account/notifications`,
        async (path, { arg }: { arg: UpdateNotificationSettingsRequest }) => {
            await http.put({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
            mutate();
        },
    );
};

interface SaveEmailTemplateRequest {
    type: EmailTemplateType;
    title: string;
    bodyHtml: string;
    regards: string;
    templateHtml: string;
    companyId: string;
}

export const useSaveEmailTemplate = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { mutate } = useAccountSettings();

    return useSWRMutation(
        `settings/account/email-template`,
        async (path, { arg }: { arg: SaveEmailTemplateRequest }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
            mutate();
        },
    );
};
